/**
 * A studio config, read structurally (PLAN §7.1a, D22): devices and where
 * they connect, mappings from layer to device, source and output layers,
 * piece types with their params, macro actions with their steps, adlib
 * panels, and the take span. The JSON editor beside it is where it changes.
 */
import type { ReactNode } from 'react';
import type { StudioSummaryView } from '../../lib/studio/config.js';
import { Badge } from '../ui/badge.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type StudioConfigSummaryProps = { readonly summary: StudioSummaryView };

function Section({ title, count, children }: { readonly title: string; readonly count: number; readonly children: ReactNode }) {
  return (
    <section className="grid gap-2" aria-label={title}>
      <h3 className="text-sm font-semibold">
        {title} <span className="font-normal text-muted-foreground">({count})</span>
      </h3>
      {count === 0 ? <p className="text-sm text-muted-foreground">None.</p> : children}
    </section>
  );
}

export function StudioConfigSummary({ summary }: StudioConfigSummaryProps) {
  return (
    <div className="grid gap-6" data-testid="studio-config-summary">
      <Section title="Devices" count={summary.devices.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Id</TableHead>
              <TableHead>Driver</TableHead>
              <TableHead>Connection</TableHead>
              <TableHead>Standby</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.devices.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-mono">{d.id}</TableCell>
                <TableCell>{d.type}</TableCell>
                <TableCell className="font-mono text-xs">{d.connection}</TableCell>
                <TableCell>{d.standbyPolicy}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="Mappings" count={summary.mappings.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Layer</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Lookahead</TableHead>
              <TableHead>One-shot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.mappings.map((m) => (
              <TableRow key={m.layer}>
                <TableCell className="font-mono">{m.layer}</TableCell>
                <TableCell className="font-mono">{m.device}</TableCell>
                <TableCell>{m.lookahead}</TableCell>
                <TableCell>{m.oneShot ? 'yes' : 'no'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Source layers" count={summary.sourceLayers.length}>
          <ul className="grid gap-1 text-sm">
            {summary.sourceLayers.map((l) => (
              <li key={l.id}>
                <span className="font-mono">{l.id}</span> {l.label === '' ? null : <span className="text-muted-foreground">— {l.label}</span>}
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Output layers" count={summary.outputLayers.length}>
          <ul className="grid gap-1 text-sm">
            {summary.outputLayers.map((l) => (
              <li key={l.id} className="flex items-center gap-2">
                <span className="font-mono">{l.id}</span>
                {l.label === '' ? null : <span className="text-muted-foreground">— {l.label}</span>}
                {l.isPgm ? <Badge variant="onair">PGM</Badge> : null}
              </li>
            ))}
          </ul>
        </Section>
      </div>
      <Section title="Piece types" count={summary.pieceTypes.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Id</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Source layer</TableHead>
              <TableHead>Lifespan</TableHead>
              <TableHead>Params</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.pieceTypes.map((t) => (
              <TableRow key={t.id} data-testid="piece-type-row">
                <TableCell className="font-mono">{t.id}</TableCell>
                <TableCell>{t.label}</TableCell>
                <TableCell className="font-mono">{t.sourceLayer}</TableCell>
                <TableCell>{t.lifespan}</TableCell>
                <TableCell className="text-xs">{t.params.length === 0 ? '–' : t.params.map((p) => `${p.name}: ${p.type}${p.required ? '*' : ''}`).join(', ')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Actions" count={summary.actions.length}>
          <ul className="grid gap-1 text-sm">
            {summary.actions.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.label === '' ? a.id : a.label}</span> <span className="text-muted-foreground">{a.steps.join(' → ')}</span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Adlib panels" count={summary.adlibPanels.length}>
          <ul className="grid gap-1 text-sm">
            {summary.adlibPanels.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.label === '' ? p.id : p.label}</span> <span className="text-muted-foreground">{p.items.join(' · ')}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>
      <p className="text-sm text-muted-foreground">
        Minimum take span: <span className="font-mono">{summary.minimumTakeSpanMs} ms</span>
      </p>
    </div>
  );
}
